import {Injectable} from '@angular/core';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';

/**
 * Service for sanitizing HTML content to prevent XSS attacks
 * Uses Angular's DomSanitizer for secure HTML rendering
 */
@Injectable({
    providedIn: 'root'
})
export class HtmlSanitizerService {

    constructor(private sanitizer: DomSanitizer) {
    }

    /**
     * Sanitizes HTML content and returns SafeHtml
     * Removes dangerous scripts, event handlers, and unsafe HTML
     *
     * @param html - Raw HTML string to sanitize
     * @returns SafeHtml - Sanitized HTML that Angular can safely render
     */
    sanitizeHtml(html: string): SafeHtml {
        if (!html) {
            return '';
        }

        // First, strip dangerous patterns manually as an extra layer
        const cleanedHtml = this.stripDangerousPatterns(html);

        // Then use Angular's built-in sanitizer
        return this.sanitizer.sanitize(1, cleanedHtml) || '';
    }

    /**
     * Strips all HTML tags and returns plain text
     * Use this for fields that should NEVER contain HTML (like names, titles)
     *
     * @param html - String that may contain HTML
     * @returns Plain text with all HTML removed
     */
    stripAllHtml(html: string): string {
        if (!html) {
            return '';
        }

        // Create a temporary div to parse HTML
        const div = document.createElement('div');
        div.innerHTML = html;

        // Return only text content
        return div.textContent || div.innerText || '';
    }

    /**
     * Validates and sanitizes user names
     * Only allows alphabetic characters, spaces, hyphens, and apostrophes
     *
     * @param name - User name to validate
     * @returns Sanitized name or empty string if invalid
     */
    sanitizeName(name: string): string {
        if (!name) {
            return '';
        }

        // Strip all HTML first
        let cleaned = this.stripAllHtml(name);

        // Remove any characters that aren't letters, spaces, hyphens, apostrophes, or periods
        cleaned = cleaned.replace(/[^a-zA-Z\s\-'.]/g, '');

        // Trim and normalize whitespace
        cleaned = cleaned.trim().replace(/\s+/g, ' ');

        return cleaned;
    }

    /**
     * Sanitizes text that may contain user mentions
     * Allows basic HTML for highlighting but strips dangerous content
     *
     * @param text - Text with possible HTML
     * @returns Sanitized HTML safe for innerHTML binding
     */
    sanitizeUserContent(text: string): SafeHtml {
        if (!text) {
            return '';
        }

        // Strip dangerous patterns
        const cleaned = this.stripDangerousPatterns(text);

        // Allow only specific safe tags for formatting
        const withWhitelistedTags = this.allowOnlySafeTags(cleaned, ['span', 'br', 'p', 'strong', 'em']);

        return this.sanitizer.sanitize(1, withWhitelistedTags) || '';
    }

    /**
     * Removes dangerous HTML patterns that could execute scripts
     *
     * @param html - HTML string to clean
     * @returns Cleaned HTML string
     */
    private stripDangerousPatterns(html: string): string {
        let cleaned = html;

        // Remove script tags and their content
        cleaned = cleaned.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');

        // Remove event handlers (onclick, onerror, onload, etc.)
        cleaned = cleaned.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
        cleaned = cleaned.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');

        // Remove javascript: protocol
        cleaned = cleaned.replace(/javascript:/gi, '');

        // Remove data: protocol (can be used for XSS)
        cleaned = cleaned.replace(/data:text\/html/gi, '');

        // Remove vbscript: protocol
        cleaned = cleaned.replace(/vbscript:/gi, '');

        // Remove iframe tags
        cleaned = cleaned.replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '');

        // Remove object and embed tags
        cleaned = cleaned.replace(/<(object|embed|applet)\b[^<]*(?:(?!<\/\1>)<[^<]*)*<\/\1>/gi, '');

        // Remove form tags
        cleaned = cleaned.replace(/<form\b[^<]*(?:(?!<\/form>)<[^<]*)*<\/form>/gi, '');

        // Remove base tags
        cleaned = cleaned.replace(/<base\b[^>]*>/gi, '');

        // Remove meta tags with http-equiv
        cleaned = cleaned.replace(/<meta\b[^>]*http-equiv[^>]*>/gi, '');

        return cleaned;
    }

    /**
     * Strips all HTML except whitelisted safe tags
     *
     * @param html - HTML to filter
     * @param allowedTags - Array of tag names to keep (e.g., ['span', 'br'])
     * @returns HTML with only whitelisted tags
     */
    private allowOnlySafeTags(html: string, allowedTags: string[]): string {
        const div = document.createElement('div');
        div.innerHTML = html;

        // Walk through all elements
        this.cleanElement(div, allowedTags);

        return div.innerHTML;
    }

    /**
     * Recursively clean DOM elements, keeping only whitelisted tags
     */
    private cleanElement(element: Element, allowedTags: string[]): void {
        const children = Array.from(element.children);

        children.forEach(child => {
            if (!allowedTags.includes(child.tagName.toLowerCase())) {
                // Replace disallowed tag with its text content
                const textNode = document.createTextNode(child.textContent || '');
                child.parentNode?.replaceChild(textNode, child);
            } else {
                // Remove all attributes except class (for styling)
                const className = child.getAttribute('class');
                Array.from(child.attributes).forEach(attr => {
                    child.removeAttribute(attr.name);
                });
                if (className) {
                    child.setAttribute('class', className);
                }

                // Recursively clean children
                this.cleanElement(child, allowedTags);
            }
        });
    }

    /**
     * Validates that a string doesn't contain HTML or script content
     *
     * @param value - String to validate
     * @returns true if safe, false if contains HTML/scripts
     */
    isSafeText(value: string): boolean {
        if (!value) {
            return true;
        }

        // Check for HTML tags
        if (/<[^>]+>/g.test(value)) {
            return false;
        }

        // Check for script-like patterns
        if (/javascript:|data:|vbscript:|on\w+\s*=/gi.test(value)) {
            return false;
        }

        return true;
    }
}

